import _get from 'lodash/get'
import _has from 'lodash/has'
import _omit from 'lodash/omit'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import md5 from 'md5'
import request from '../request'
import { TIMEZONE_MOUNTAIN } from '../constants'
import {
  parseSpeakerLinkUrl,
  applySpeakerUrlToPayload,
} from '../../gui/components/live-event-management/speakerLinkUrl'
import { createAuthorization } from '../auth'

const SPEAKERS_ENDPOINT = 'live-access-speakers'
const SPEAKERS_BIO_ENDPOINT = 'live-access-speakers-bios'
const EVENT_LIST_ENDPOINT = 'live-access-events-all'
const EVENT_DETAILS_ENDPOINT = 'live-access-events'
const CHATS_BLOCKED_USERS_ENDPONT = 'live-access-chats/blocked-users'
const EVENT_TRACKS_ENDPOINT = 'live-access-events/tracks'
const EVENT_TRACK_ENDPOINT = 'live-access-events/track'
const EVENT_TRANSLATION_ENDPOINT = 'live-access-events/translation'
const EVENT_SCHEDULE_ENDPOINT = 'live-access-events/schedule'
const EVENT_SESSION_ENDPOINT = 'live-access-events/session'
const EVENT_SPOTLIGHT_ENDPOINT = 'live-access-events/spotlight'
const EVENT_SESSION_TRANSLATION_ENDPOINT = 'live-access-events/session/translations'
const EVENT_CHATS_ENDPOINT = 'live-access-chats'
const EVENT_CREATE_CHAT_ENDPOINT = 'live-access-chat'
const START_STREAM_ENDPOINT = 'media-live-channel/start'
const STOP_STREAM_ENDPOINT = 'media-live-channel/stop'
const MEDIALIVE_METRICS = 'media-live-channel/metrics'

/**
 * Fetches viewer UIDs from Youbora Analytics API
 *
 * API Details (based on official documentation):
 * - Endpoint: https://api.youbora.com/{accountCode}/data
 * - Authentication: MD5 token-based (dateToken + API key)
 * - Parameters:
 *   - metrics: views (required) - Using 'views' to match manual report which uses "Plays"
 *     Note: 'views' with groupBy=user returns all viewer UIDs. 'uniques' may return fewer results for some events.
 *   - type: live (required - applies to both LIVE and AMA events from Youbora's perspective)
 *   - groupBy: user (required to get per-user data)
 *   - orderBy: (not used) - Removed to avoid potential filtering. Results are returned in API's default order.
 *   - orderDirection: (not used) - Removed along with orderBy
 *   - limit: Maximum number of results per request (MAX: 2000 per API documentation)
 *   - offset: For pagination (optional, int64)
 *   - fromDate/toDate: ISO 8601 date range (required)
 *   - version: Response format version (optional, defaults to 1)
 * Note: 'decimals' parameter was removed as it's not documented for /data endpoint
 *
 * Known Limitations:
 * - Maximum limit per request is 2000 (per official API documentation)
 * - Pagination is required to retrieve all results when there are more than 2000 UIDs
 * - The API may return empty user fields for anonymous/unauthenticated viewers
 *
 * @param {Object} params - Query parameters
 * @param {string|Date} params.startDate - Start date for the query
 * @param {string|Date} params.endDate - End date for the query
 * @returns {Promise<Array>} Array of user data objects from Youbora
 */
export async function getUidsFromYoubora ({ startDate, endDate }) {
  const YOUBORA_ACCOUNT_CODE = 'gaiam'
  const YOUBORA_API_KEY = 'gaiam2015'
  const TTL = 31536000000 // 1 year
  // Note: Documentation shows https://api.npaw.com but code uses api.youbora.com
  // These may be aliases, but if issues persist, consider switching to api.npaw.com
  const YOUBORA_BASE_URL = 'https://api.youbora.com'
  // Per official API documentation for /data endpoint: Maximum limit is 2000
  // https://documentation.npaw.com/integration-docs/reference/get_accountcode-data
  // Note: The /rawdata/generic endpoint has a limit of 500, but this code uses /data which has limit 2000
  const YOUBORA_LIMIT = 2000

  // Convert dates to ISO 8601 format as required by API
  // Documentation also supports preset periods like 'last6hours', but we use explicit dates
  const sdate = encodeURIComponent(new Date(startDate).toISOString())
  const edate = encodeURIComponent(new Date(endDate).toISOString())
  const expiration = Date.now() + TTL

  // Collect all results across multiple pages
  const allValues = []
  let offset = 0
  let hasMore = true
  let pageCount = 0
  const MAX_PAGES = 1000 // Safety limit to prevent infinite loops
  const usedOffsets = new Set() // Track used offsets to prevent infinite loops
  let requestCurl = null
  let requestDates = null

  // eslint-disable-next-line no-await-in-loop
  while (hasMore && pageCount < MAX_PAGES) {
    pageCount += 1
    // Note: 'decimals' parameter was removed as it's not documented for /data endpoint
    // Using 'views' metric to match manual report which shows "Plays"
    // 'views' with groupBy=user returns all viewer UIDs per event
    // Removed 'orderBy' and 'orderDirection' to avoid potential filtering - results returned in API's default order
    // Valid metrics per documentation: views, uniques, playtime, hours, joinTime, bitrate, etc.
    // Documentation shows these valid parameters: metrics, type, groupBy, orderBy, limit, orderDirection, fromDate, toDate, offset, version
    const url = `/${YOUBORA_ACCOUNT_CODE}/data?metrics=views&type=live&groupBy=user&limit=${YOUBORA_LIMIT}&fromDate=${sdate}&toDate=${edate}&offset=${offset}&version=1`
    const preUrl = `${url}&dateToken=${expiration}`
    const mystring = Buffer.from(`${preUrl}${YOUBORA_API_KEY}`, 'utf-8').toString()
    const token = md5(mystring)
    const requestUrl = `${YOUBORA_BASE_URL}${preUrl}&token=${token}`

    if (pageCount === 1) {
      requestCurl = `curl -s -H "Accept: application/json" -H "x-client-attributes: app-provider/gaia,app/admin" "${requestUrl}"`
      requestDates = { startDate, endDate, fromDateInUrl: sdate, toDateInUrl: edate }
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await request.get(requestUrl).set('accept', 'application/json')

      // Log full response structure for debugging API changes (only on first page)
      const responseBody = _get(res, 'body', {})
      const dataArray = _get(res, 'body.data', [])
      const values = _get(res, 'body.data.0.metrics.0.values', [])
      // Get the offset from the API response - this indicates the next page offset
      const responseOffset = _get(res, 'body.data.0.offset', null)

      if (pageCount === 1) {
        // eslint-disable-next-line no-console
        console.log('Youbora API Response Structure:', {
          hasBody: !!responseBody,
          bodyKeys: responseBody ? Object.keys(responseBody) : [],
          dataArrayLength: Array.isArray(dataArray) ? dataArray.length : 'not an array',
          valuesLength: Array.isArray(values) ? values.length : 'not an array',
          responseOffset,
          limit: YOUBORA_LIMIT,
          firstDataItem: dataArray && dataArray[0] ? Object.keys(dataArray[0]) : null,
          fullResponseStructure: JSON.stringify(responseBody).substring(0, 2000),
          requestUrl: requestUrl.replace(YOUBORA_API_KEY, '***HIDDEN***'),
          // Log the full first data item to see all available fields
          firstDataItemFull:
            dataArray && dataArray[0] ? JSON.stringify(dataArray[0]).substring(0, 2000) : null,
        })
      }

      // Check response structure - Youbora API might have changed
      if (!Array.isArray(values)) {
        // eslint-disable-next-line no-console
        console.error('Youbora response structure unexpected:', {
          body: responseBody,
          dataPath: 'body.data.0.metrics.0.values',
          actualValue: values,
          fullResponse: JSON.stringify(responseBody).substring(0, 2000),
        })
        break
      }

      // Filter out empty user entries (API sometimes returns {"user": "", "value": 0} when there are no more results)
      const validValues = values.filter((v) => v.user && v.user.trim() !== '')

      // Add valid values from this page
      if (validValues.length > 0) {
        allValues.push(...validValues)
        // eslint-disable-next-line no-console
        console.log(
          `Youbora API Page ${pageCount}: Retrieved ${validValues.length} valid UIDs (${values.length} total entries, ${values.length - validValues.length} empty) (Total so far: ${allValues.length})`,
        )
      } else if (values.length > 0) {
        // We got entries but they're all empty - this means we've reached the end
        // eslint-disable-next-line no-console
        console.log(
          `Youbora API Page ${pageCount}: Got ${values.length} entries but all have empty user fields, reached end`,
        )
      }

      // Check if there are more results
      // First, if we got no values or only empty user entries, we've reached the end
      if (values.length === 0 || validValues.length === 0) {
        // eslint-disable-next-line no-console
        console.log(
          `Youbora API Page ${pageCount}: No valid values returned (${values.length} total, ${validValues.length} valid), reached end`,
        )
        hasMore = false
      } else {
        // The API response offset indicates where the next page should start
        // The API may use a different pagination scheme than calculated offset.
        // Try using response offset directly first, then fall back to calculated offset if response offset doesn't work
        const calculatedOffset = offset + validValues.length
        let nextOffset = calculatedOffset

        // If response provides an offset and it's different from calculated, try response offset first
        // The API may use a different pagination method than simple offset incrementing
        if (
          responseOffset !== null &&
          responseOffset !== undefined &&
          responseOffset > calculatedOffset &&
          !usedOffsets.has(responseOffset)
        ) {
          // Try response offset - it might be the correct next page start
          nextOffset = responseOffset
        }

        // Check if we should continue pagination
        // Continue if:
        // 1. We got a full page (validValues.length >= limit), OR
        // 2. Response offset suggests more results (responseOffset > offset)
        const shouldContinue =
          validValues.length >= YOUBORA_LIMIT ||
          (responseOffset !== null && responseOffset !== undefined && responseOffset > offset)

        if (shouldContinue && !usedOffsets.has(nextOffset)) {
          // eslint-disable-next-line no-console
          console.log(
            `Youbora API Page ${pageCount}: Got ${validValues.length} valid values, continuing to next page. Current offset: ${offset}, Next offset: ${nextOffset} (${nextOffset === responseOffset ? 'from response' : 'calculated'}), Response offset: ${responseOffset}`,
          )
          usedOffsets.add(offset) // Track the offset we just used
          offset = nextOffset
          // Small delay to avoid rate limiting
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve()
            }, 100)
          })
        } else {
          // No more results - either we got less than limit and response doesn't suggest more, or we've seen this offset
          // eslint-disable-next-line no-console
          console.log(
            `Youbora API Page ${pageCount}: Stopping pagination - Got ${validValues.length} valid values, next offset: ${nextOffset}, response offset: ${responseOffset}, shouldContinue: ${shouldContinue}, offsetUsed: ${usedOffsets.has(nextOffset)}`,
          )
          hasMore = false
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Youbora API Error on page ${pageCount}:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        body: error.response?.body,
        requestUrl: requestUrl.replace(YOUBORA_API_KEY, '***HIDDEN***'),
      })
      // If we have some results, return what we have; otherwise throw
      if (allValues.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(`Returning ${allValues.length} UIDs retrieved before error occurred`)
        break
      }
      throw error
    }
  }

  // eslint-disable-next-line no-console
  console.log(
    `Youbora API Complete: Retrieved ${allValues.length} total UIDs across ${pageCount} page(s)`,
  )

  if (pageCount >= MAX_PAGES) {
    // eslint-disable-next-line no-console
    console.warn(`Reached maximum page limit (${MAX_PAGES}). There may be more UIDs available.`)
  }

  return { uids: allValues, requestCurl, requestDates }
}

export async function getUserEmailByUid ({ auth, server, uid }) {
  const res = await request
    .get(`${server}/v1/user/uid/${uid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function mediaLiveMetrics ({
  auth,
  server,
  id,
  name,
  statistics,
  unit,
  pipeline,
  spanMinutes,
  periodSeconds,
}) {
  const res = await request
    .get(
      `${server}/${MEDIALIVE_METRICS}/${id}?name=${name}&statistics=${statistics}&unit=${unit}&pipeline=${pipeline}&spanMinutes=${spanMinutes}&periodSeconds=${periodSeconds}`,
    )
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function startLiveStream ({ auth, server, id }) {
  const res = await request
    .post(`${server}/${START_STREAM_ENDPOINT}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function stopLiveStream ({ auth, server, id }) {
  const res = await request
    .post(`${server}/${STOP_STREAM_ENDPOINT}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get speakers list from Brooklyn
 */
export async function getSpeakerList ({ auth, server }) {
  const res = await request
    .get(`${server}/${SPEAKERS_ENDPOINT}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get event summaries from Brooklyn
 * @param {Object} options - Options for fetching event list
 * @param {Object} options.auth - Authentication object
 * @param {string} options.server - Server URL
 * @param {string} [options.type] - Event type filter ('LIVE' or 'AMA')
 */
export async function getEventList ({ auth, server, type }) {
  const url = type
    ? `${server}/${EVENT_LIST_ENDPOINT}?type=${type}`
    : `${server}/${EVENT_LIST_ENDPOINT}`
  const res = await request
    .get(url)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get event details from Brooklyn
 */
export async function getEventDetails ({ auth, server, eventId, language }) {
  const res = await request
    .get(`${server}/${EVENT_DETAILS_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get event translation from Brooklyn
 */
export async function getEventTranslation ({ auth, server, eventId, language }) {
  const res = await request
    .get(`${server}/${EVENT_TRANSLATION_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get event tracks from Brooklyn
 */
export async function getEventTracks ({ auth, server, eventId, language }) {
  const res = await request
    .get(`${server}/${EVENT_TRACKS_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function getTrack ({ auth, server, trackId, language }) {
  const res = await request
    .get(`${server}/${EVENT_TRACK_ENDPOINT}/${trackId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function updateEventTrack ({ auth, server, trackId, language, body }) {
  const res = await request
    .put(`${server}/${EVENT_TRACK_ENDPOINT}/${trackId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export async function getSchedule ({ auth, server, trackId, language }) {
  const res = await request
    .get(`${server}/${EVENT_SCHEDULE_ENDPOINT}/${trackId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function updateSession ({ auth, server, sessionId, language, body }) {
  const res = await request
    .put(`${server}/${EVENT_SESSION_ENDPOINT}/${sessionId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export async function updateSessionTranslation ({ auth, server, sessionId, language, body }) {
  const res = await request
    .put(`${server}/${EVENT_SESSION_TRANSLATION_ENDPOINT}/${sessionId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export async function createSession ({ auth, server, language, body }) {
  const res = await request
    .post(`${server}/${EVENT_SESSION_ENDPOINT}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Update event details in Brooklyn
 */
export async function updateEventDetails ({ auth, server, eventId, language, body }) {
  const res = await request
    .put(`${server}/${EVENT_DETAILS_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Block chat user in Brooklyn
 */
export async function blockChatUser ({ auth, server, channel, username }) {
  const res = await request
    .post(`${server}/${CHATS_BLOCKED_USERS_ENDPONT}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ channel, username })
  return _get(res, 'body', {})
}

/**
 * Unblock chat user in Brooklyn
 */
export async function unBlockChatUser ({ auth, server, channel, username }) {
  const res = await request
    .delete(`${server}/${CHATS_BLOCKED_USERS_ENDPONT}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send({ channel, username })
  return _get(res, 'body', {})
}

export async function getChatBlockedUsersList ({ auth, server, channel }) {
  const res = await request
    .get(`${server}/${CHATS_BLOCKED_USERS_ENDPONT}/${channel}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Update event translation in Brooklyn
 */
const EVENT_TRANSLATION_PROMO_KEYS = ['promoUrl', 'promo_url', 'promoVideoId']

export async function updateTranslation ({ auth, server, eventId, language, body }) {
  const sanitizedBody = _omit(body, EVENT_TRANSLATION_PROMO_KEYS)
  const res = await request
    .put(`${server}/${EVENT_TRANSLATION_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(sanitizedBody)
  return _get(res, 'body', {})
}

/**
 * Remove event translation copy for one locale (does not delete the event).
 */
export async function deleteEventTranslation ({ auth, server, eventId, language }) {
  const res = await request
    .delete(`${server}/${EVENT_TRANSLATION_ENDPOINT}/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Remove spotlight translation copy for one locale (does not delete the spotlight shell).
 */
export async function deleteSpotlightTranslation ({ auth, server, spotLightId, language }) {
  const res = await request
    .delete(`${server}/${EVENT_SPOTLIGHT_ENDPOINT}/${spotLightId}/translation?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function createEvent ({ auth, server, event }) {
  const { primaryLanguage } = event
  const res = await request
    .post(`${server}/${EVENT_DETAILS_ENDPOINT}?language=${primaryLanguage}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(event)
  return _get(res, 'body', {})
}

export async function createTrack ({ auth, server, track }) {
  const { language } = track
  const res = await request
    .post(`${server}/${EVENT_TRACK_ENDPOINT}?language=${language || 'en'}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(track)
  return _get(res, 'body', {})
}

export async function createEventChat ({ auth, server, body }) {
  const res = await request
    .post(`${server}/${EVENT_CREATE_CHAT_ENDPOINT}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export const EVENT_DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm"

export function buildEventDateTimeWithTimezone ({ eventDate, eventTimezone }) {
  if (eventDate) {
    const result = zonedTimeToUtc(`${eventDate}`, `${eventTimezone || TIMEZONE_MOUNTAIN}`)

    return result.toISOString()
  }
  return null
}

/**
 * UTC ISO from API → datetime-local value (Mountain wall clock) for admin forms.
 * @param {string | Date | null | undefined} utcIso
 * @returns {string}
 */
export function formatEventDateTimeForInput (utcIso) {
  if (utcIso == null || utcIso === '') {
    return ''
  }
  try {
    return formatInTimeZone(utcIso, TIMEZONE_MOUNTAIN, EVENT_DATETIME_LOCAL_FORMAT)
  } catch {
    return ''
  }
}

/**
 * datetime-local value from admin form → UTC ISO for Brooklyn (Mountain wall clock).
 * @param {string | null | undefined} datetimeLocal
 * @returns {string | null}
 */
export function parseEventDateTimeFromInput (datetimeLocal) {
  return buildEventDateTimeWithTimezone({
    eventDate: datetimeLocal,
    eventTimezone: TIMEZONE_MOUNTAIN,
  })
}

/**
 * Normalize admin form value or API ISO to UTC ISO.
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
export function toEventUtcIso (value) {
  if (value == null || value === '') {
    return null
  }
  const str = String(value).trim()
  if (str.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(str)) {
    return new Date(str).toISOString()
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) {
    return parseEventDateTimeFromInput(str)
  }
  return parseEventDateTimeFromInput(str) || new Date(str).toISOString()
}

export function formatEvent (event) {
  return {
    startDate: _get(event, 'startDate'),
    endDate: _get(event, 'endDate'),
    title: _get(event, 'title'),
    primaryLanguage: _get(event, 'primaryLanguage'),
    route: _get(event, 'route'),
    featured: _get(event, 'featured'),
    summaryEnabled: _get(event, 'summaryEnabled', false),
    webviewUrl: _get(event, 'webviewUrl'),
    type: _get(event, 'type'),
  }
}

export function formatSession (session) {
  return {
    startDate: _get(session, 'startDate'),
    endDate: _get(session, 'endDate'),
    title: _get(session, 'title'),
    eventId: _get(session, 'eventId'),
    trackId: _get(session, 'trackId'),
    isLiveStream: _get(session, 'isLiveSession', true),
    isFree: _get(session, 'isFree', false),
    ...(_has(session, 'allowAnon') && { allowAnon: _get(session, 'allowAnon') }),
  }
}

export function formatSpeaker (data) {
  const speaker = {
    honorific: _get(data, 'honorific'),
    firstName: _get(data, 'firstName'),
    middleName: _get(data, 'middleName'),
    lastName: _get(data, 'lastName'),
    bio: _get(data, 'bio'),
    headshot: _get(data, 'headshot'),
  }
  const urlParsed = parseSpeakerLinkUrl(_get(data, 'url'))
  if (urlParsed.valid && urlParsed.value != null) {
    speaker.url = urlParsed.value
  }
  return speaker
}

/**
 * Get Event Chat info for an event from Brooklyn
 */
export async function getEventChatInfoData ({ auth, server, eventId }) {
  const res = await request
    .get(`${server}/${EVENT_CHATS_ENDPOINT}/${eventId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * Get Event Chat info for an event from Brooklyn
 */
export async function getMediaLiveChannelInfoData ({ auth, url, id }) {
  const res = await request
    .get(`${url}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function updateMediaLiveChannelData ({ auth, url, id, body }) {
  const res = await request
    .put(`${url}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Get Event Spotlight using EventId from Brooklyn
 */
export async function getEventSpotlightViaEventId ({ auth, server, eventId, language }) {
  const res = await request
    .get(`${server}/${EVENT_SPOTLIGHT_ENDPOINT}/event/${eventId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

/**
 * To create a spotlight for an event do a POST with the event_id in the body.
 * You will be returned a spotlight UUID.
 */
export async function createEventSpotlight ({ auth, server, body, language = 'en' }) {
  const res = await request
    .post(`${server}/${EVENT_SPOTLIGHT_ENDPOINT}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

// To add translation text/images with a PUT to the spotlight
// with a different language using Spotlight uuid
export async function updateSpotlight ({ auth, server, spotLightId, language, body }) {
  const res = await request
    .put(`${server}/${EVENT_SPOTLIGHT_ENDPOINT}/${spotLightId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export async function createNewSpeaker ({ auth, server, speaker }) {
  const payload = applySpeakerUrlToPayload(speaker, { omitNull: true })
  const res = await request
    .post(`${server}/${SPEAKERS_ENDPOINT}?language=en`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(payload)
  return _get(res, 'body', {})
}

export async function updateSpeaker ({ auth, server, speakerId, body }) {
  const payload = applySpeakerUrlToPayload({ ...body }, { omitNull: false })
  const res = await request
    .put(`${server}/${SPEAKERS_ENDPOINT}/${speakerId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(payload)
  return _get(res, 'body', {})
}

export async function updateSpeakerBio ({ auth, server, speakerId, language, body }) {
  const res = await request
    .put(`${server}/${SPEAKERS_BIO_ENDPOINT}/${speakerId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

export async function deleteSpeakerBio ({ auth, server, speakerId, language }) {
  await request
    .delete(`${server}/${SPEAKERS_BIO_ENDPOINT}/${speakerId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
}

export async function getSpeaker ({ auth, server, speakerId, language }) {
  const res = await request
    .get(`${server}/${SPEAKERS_ENDPOINT}/${speakerId}?language=${language}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return _get(res, 'body', {})
}

export async function deleteSessionData ({ auth, server, body }) {
  const { id } = body
  await request
    .delete(`${server}/${EVENT_SESSION_ENDPOINT}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
}

export async function deleteEventData ({ auth, server, body }) {
  const { id } = body
  await request
    .delete(`${server}/${EVENT_DETAILS_ENDPOINT}/${id}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
}
